/**
 * Terminal summary once a run finishes.
 *
 * The reconcile note is permanent, not conditional: the API returns success
 * even when the Stripe charge was not paid, so "submitted" always needs the
 * Verified column to back it up.
 */

import React from 'react';
import { Button, Message, Segment } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { i18nKeys } from '../constants';
import styles from '../BulkChargeGuarantee.module.css';

interface ResultsSummaryProps {
  counts: {
    submitted: number;
    rejected: number;
    indeterminate: number;
    blocked: number;
  };
  wasStopped: boolean;
  abortedByBreaker: boolean;
  onStartNewBatch: () => void;
}

const ResultsSummary: React.FC<ResultsSummaryProps> = ({
  counts,
  wasStopped,
  abortedByBreaker,
  onStartNewBatch
}) => (
  <Segment>
    <h4>
      <Translate value={i18nKeys.RESULTS.TITLE} />
    </h4>

    <p>{I18n.t(i18nKeys.RESULTS.COUNTS, counts)}</p>

    {abortedByBreaker && (
      <Message negative>
        <p>
          <Translate value={i18nKeys.RESULTS.BREAKER_NOTE} />
        </p>
      </Message>
    )}

    {wasStopped && !abortedByBreaker && (
      <Message warning>
        <p>
          <Translate value={i18nKeys.RESULTS.STOPPED_NOTE} />
        </p>
      </Message>
    )}

    {counts.indeterminate > 0 && (
      <Message negative>
        <p>
          <Translate value={i18nKeys.RESULTS.INDETERMINATE_NOTE} />
        </p>
      </Message>
    )}

    <Message info>
      <p>
        <Translate value={i18nKeys.RESULTS.RECONCILE_NOTE} />
      </p>
    </Message>

    {/*
      There is deliberately no download here. Every export is per charity and
      lives on that charity's header in the table above, so there is no file
      anywhere that mixes two charities together and nothing to pick the wrong
      one from.
    */}
    <p className={styles.downloadNote}>
      <Translate value={i18nKeys.RESULTS.DOWNLOAD_NOTE} />
    </p>

    <div className={styles.resultActions}>
      <Button onClick={onStartNewBatch}>
        <Translate value={i18nKeys.RESULTS.START_NEW_BATCH} />
      </Button>
    </div>
  </Segment>
);

export default ResultsSummary;
